import { getProxyUrl, proxyResponse } from '.'
import { addOptionsLang } from './I18N'

const TOKEN_KEY = 'access_token'
const REMEMBER_SESSION_KEY = 'remember_session'

class AutenticateProxy {
  constructor() {
    this.url = getProxyUrl('api/auth')
  }

  isAutenticated() {
    return proxyResponse(fetch(this.url + '/isAuthenticated', addOptionsAuth({ mode: 'cors' })))
  }

  getUserAuthenticated() {
    return proxyResponse(
      fetch(this.url + '/getUserAuthenticated', addOptionsAuth({ mode: 'cors' })),
    )
  }

  getUserAct() {
    return proxyResponse(fetch(this.url + '/getUserAct', addOptionsAuth({ mode: 'cors' })))
  }

  newSession() {
    setRemeberSession()
    return proxyResponse(
      fetch(this.url + '/newSession', addOptionsLang(addOptionsAuth({ mode: 'cors' }))),
    )
      .then((data) => {
        if (data.token) setAuthToken(data.token) //este data.token nos da el de public todo el rato
        if (data.cleanRembemberToken) cleanRememberSession()
        // data.token = undefined
        // data.cleanRembemberToken = undefined

        return data
      })
  }

  autenticate(p) {
    return proxyResponse(
      fetch(
        this.url + '/authenticate',
        addOptionsLang({
          method: 'POST',
          body: JSON.stringify(p),
          headers: { mode: 'cors', 'Content-Type': 'application/json' },
        }),
      ),
    ).then((data) => {
      if (data.token) setAuthToken(data.token)
      if (data.rememberToken) setRememberSession(data.rememberToken)

      return data
    })
  }

  authenticateWithTokenF2H(tokenF2H) {
    const payload = {
      public: false,
      remember: false,
      accessToken: tokenF2H,
    }

    return proxyResponse(
      fetch(this.url + '/authenticate', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { mode: 'cors', 'Content-Type': 'application/json' },
      }),
    ).then((data) => {
      if (data.token) {
        setAuthToken(data.token)
      }

      if (data.rememberToken) {
        setRememberSession(data.rememberToken)
      }

      return data
    })
  }

  register(p) {
    return proxyResponse(
      fetch(
        this.url + '/register',
        addOptionsLang(
          addOptionsAuth({
            method: 'POST',
            body: JSON.stringify(p),
            headers: { mode: 'cors', 'Content-Type': 'application/json' },
          }),
        ),
      ),
    )
  }

  modifyUserAct(p) {
    return proxyResponse(
      fetch(
        this.url + '/modifyUserAct',
        addOptionsLang(
          addOptionsAuth({
            method: 'POST',
            body: JSON.stringify(p),
            headers: { mode: 'cors', 'Content-Type': 'application/json' },
          }),
        ),
      ),
    ).then((data) => {
      if (data.token) setAuthToken(data.token)

      if (hasRememberSession) {
        if (data.cleanRembemberToken) cleanRememberSession()
        else if (data.rememberToken) setRememberSession(data.rememberToken)
      }
      // data.token = undefined
      // data.rememberToken = undefined
      // data.cleanRembemberToken = undefined

      return data
    })
  }

  logOff() {
    cleanAuthToken()
  }
}

function setAuthToken(token) {
  sessionStorage.setItem(TOKEN_KEY, token)
}

function cleanAuthToken() {
  sessionStorage.removeItem(TOKEN_KEY)
}

function setRememberSession(token) {
  localStorage.setItem(REMEMBER_SESSION_KEY, token)
}

function cleanRememberSession() {
  localStorage.removeItem(REMEMBER_SESSION_KEY)
}

function hasRememberSession() {
  const token = localStorage.getItem(REMEMBER_SESSION_KEY)

  return token != null
}

function getParamsForNewSession() {
  const p = {}
  const token = localStorage.getItem(REMEMBER_SESSION_KEY)

  if (token) p.rememberToken = token

  return p
}

function setRemeberSession() {
  const token = localStorage.getItem(REMEMBER_SESSION_KEY)
  if (!token) {
    // Público / sin remember: borra access_token ANTES de pedir newSession.
    // Si newSession falla o setAuthToken no puede escribir, la sesión queda sin token.
    cleanAuthToken()
  } else {
    setAuthToken(token)
  }
}

function addOptionsAuth(p) {
  const p1 = p || {}
  const token = sessionStorage.getItem(TOKEN_KEY)

  if (token != null) {
    if (!p1.headers) p1.headers = {}
    p1.headers.Authorization = 'Ninpo ' + token
  }

  return p1
}

let refreshSessionInFlight: Promise<string | null> | null = null

/**
 * Renueva el access_token vía newSession (remember_session o sesión Public).
 * Evita llamadas concurrentes y notifica a SignalR / listeners con `authChange`.
 */
async function refreshSessionToken(): Promise<string | null> {
  if (refreshSessionInFlight) return refreshSessionInFlight

  refreshSessionInFlight = (async () => {
    try {
      const previous = sessionStorage.getItem(TOKEN_KEY)
      const data = await new AutenticateProxy().newSession()
      const next =
        (data && typeof data.token === 'string' && data.token) ||
        sessionStorage.getItem(TOKEN_KEY)

      if (next && next !== previous) {
        window.dispatchEvent(new Event('authChange'))
      }
      return next
    } catch (error) {
      console.warn('[auth] No se pudo renovar la sesión:', error)
      return null
    } finally {
      refreshSessionInFlight = null
    }
  })()

  return refreshSessionInFlight
}

export {
  AutenticateProxy,
  addOptionsAuth,
  cleanAuthToken,
  cleanRememberSession,
  refreshSessionToken,
  setAuthToken,
  setRememberSession,
}
export default {
  setAuthToken,
  cleanAuthToken,
  setRememberSession,
  cleanRememberSession,
  addOptionsAuth,
  refreshSessionToken,
  AutenticateProxy,
}
